import { Icon } from '@/components/ui/icon';
import { cn } from '@/lib/utils';
import { KeyPicker } from './key-picker';

export const FONT_MIN = 14;
export const FONT_MAX = 32;
export const SPEED_MAX = 5;

const Group: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className,
}) => (
  <div
    className={cn(
      'flex shrink-0 items-center gap-1 rounded-[12px] border border-border bg-sunken p-1',
      className,
    )}
  >
    {children}
  </div>
);

const IconButton: React.FC<{
  icon: React.ComponentProps<typeof Icon>['name'];
  label: string;
  onClick: () => void;
  active?: boolean;
  disabled?: boolean;
}> = ({ icon, label, onClick, active, disabled }) => (
  <button
    type="button"
    aria-label={label}
    title={label}
    disabled={disabled}
    onClick={onClick}
    className={cn(
      'flex size-[34px] items-center justify-center rounded-[9px] text-muted-foreground transition-colors hover:bg-raised hover:text-white disabled:opacity-30',
      active && 'bg-raised text-white',
    )}
  >
    <Icon name={icon} className="size-4" />
  </button>
);

const Readout: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className,
}) => (
  <span
    className={cn(
      'min-w-[64px] px-1 text-center font-bold text-[12px] text-white uppercase tracking-wider',
      className,
    )}
  >
    {children}
  </span>
);

export type SongControlsProps = {
  /** Tom de referência (do repertório ou original) e tom exibido. */
  baseKey: string;
  shownKey: string;
  transpose: number;
  onTranspose: (steps: number) => void;
  capo: number;
  onCapo: (capo: number) => void;
  fontSize: number;
  onFontSize: (size: number) => void;
  scrolling: boolean;
  onToggleScroll: () => void;
  speed: number;
  onSpeed: (speed: number) => void;
  showChords: boolean;
  onToggleChords: () => void;
  showDiagrams: boolean;
  onToggleDiagrams: () => void;
  onPrint: () => void;
  className?: string;
};

const signed = (n: number) => (n > 0 ? `+${n}` : String(n));

/** Controles da cifra, iguais nos 3 modos (só o container muda). */
export const SongControls: React.FC<SongControlsProps> = ({
  baseKey,
  shownKey,
  transpose,
  onTranspose,
  capo,
  onCapo,
  fontSize,
  onFontSize,
  scrolling,
  onToggleScroll,
  speed,
  onSpeed,
  showChords,
  onToggleChords,
  showDiagrams,
  onToggleDiagrams,
  onPrint,
  className,
}) => (
  <div className={cn('flex items-center gap-2 overflow-x-auto', className)}>
    <Group>
      <IconButton
        icon="minus"
        label="Baixar um semitom"
        onClick={() => onTranspose(transpose - 1)}
      />
      <KeyPicker base={baseKey} current={shownKey} onPick={onTranspose}>
        Tom {shownKey}
        {transpose !== 0 && (
          <span className="ml-1 text-accent-soft">{signed(transpose)}</span>
        )}
      </KeyPicker>
      <IconButton
        icon="plus"
        label="Subir um semitom"
        onClick={() => onTranspose(transpose + 1)}
      />
    </Group>

    <Group>
      <IconButton
        icon="minus"
        label="Capo mais baixo"
        disabled={capo <= 0}
        onClick={() => onCapo(capo - 1)}
      />
      <Readout>{capo > 0 ? `Capo ${capo}ª casa` : 'Sem capo'}</Readout>
      <IconButton
        icon="plus"
        label="Capo mais alto"
        disabled={capo >= 12}
        onClick={() => onCapo(capo + 1)}
      />
    </Group>

    <Group>
      <button
        type="button"
        aria-label="Diminuir letra"
        disabled={fontSize <= FONT_MIN}
        onClick={() => onFontSize(fontSize - 2)}
        className="flex size-[34px] items-center justify-center rounded-[9px] font-bold text-[12px] text-muted-foreground hover:bg-raised hover:text-white disabled:opacity-30"
      >
        A−
      </button>
      <Readout className="min-w-[36px]">{fontSize}</Readout>
      <button
        type="button"
        aria-label="Aumentar letra"
        disabled={fontSize >= FONT_MAX}
        onClick={() => onFontSize(fontSize + 2)}
        className="flex size-[34px] items-center justify-center rounded-[9px] font-bold text-[15px] text-muted-foreground hover:bg-raised hover:text-white disabled:opacity-30"
      >
        A+
      </button>
    </Group>

    <Group>
      <button
        type="button"
        aria-label={scrolling ? 'Pausar rolagem' : 'Iniciar rolagem'}
        title="Rolagem automática (espaço)"
        onClick={onToggleScroll}
        className={cn(
          'flex size-[34px] items-center justify-center rounded-[9px] transition-colors',
          scrolling
            ? 'bg-success/15 text-success'
            : 'text-muted-foreground hover:bg-raised hover:text-white',
        )}
      >
        <Icon name={scrolling ? 'pause' : 'play'} className="size-4" />
      </button>
      <button
        type="button"
        aria-label={`Velocidade ${speed} de ${SPEED_MAX}`}
        title="Velocidade da rolagem"
        onClick={() => onSpeed(speed >= SPEED_MAX ? 1 : speed + 1)}
        className="flex h-[34px] items-end gap-[3px] rounded-[9px] px-2.5 pb-2.5 hover:bg-raised"
      >
        {Array.from({ length: SPEED_MAX }, (_, i) => (
          <span
            key={i}
            className={cn(
              'w-[3px] rounded-full',
              i < speed ? 'bg-accent-ink' : 'bg-border-strong',
            )}
            style={{ height: 5 + i * 3 }}
          />
        ))}
      </button>
    </Group>

    <Group>
      <IconButton
        icon="music-4"
        label="Mostrar cifra"
        active={showChords}
        onClick={onToggleChords}
      />
      <IconButton
        icon="layout-grid"
        label="Mostrar diagramas"
        active={showDiagrams}
        onClick={onToggleDiagrams}
      />
      <IconButton icon="printer" label="Imprimir" onClick={onPrint} />
    </Group>
  </div>
);
